import { useCallback } from 'react';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
// eslint-disable-next-line @change/sibling-module-imports
import type { FacebookPost, RedditPost } from 'src/app/pages/starterDashboardV2/shared/components/SharingModal/types';
import { type Recommendation, useSharingModal } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { RootShareRecommendations } from 'src/app/pages/starterDashboardV2/shared/hooks';

import { useShareRecommendationsTracking } from '../../../hooks/useShareRecommendationsTracking';

type Platform = keyof RootShareRecommendations;

type UseSocialGroupCardClickProps = {
	platform: Platform;
	viewContext: 'home' | 'share';
};

type UseSocialGroupCardClickReturn = {
	handleLinkClick: (recommendation: Recommendation, location: string, context: string) => void;
};

export function useSocialGroupCardClick({
	platform,
	viewContext,
}: UseSocialGroupCardClickProps): UseSocialGroupCardClickReturn {
	const {
		actions: { handleLinkClick: trackLinkClick },
	} = useShareRecommendationsTracking();
	const {
		data: {
			petition,
			viewer: {
				experiments: { starterExperienceReadyToSharePredrafted },
			},
		},
	} = useStarterDashboardV2PageData();
	const { startSharingFlow } = useSharingModal();

	const handleLinkClick = useCallback(
		(rec: Recommendation, location: string, context: string) => {
			trackLinkClick(rec.name, location, rec.metadata.source, context);
			if (starterExperienceReadyToSharePredrafted.variation === 'enabled') {
				// This type assertion is needed to allow the recommendedGroup prop,
				// as typescript inference is not able to infer the correct type based on the platform and post type.
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
				startSharingFlow({
					platform,
					postType: 'post',
					petition,
					recommendedGroup: rec,
					trackingProps: {
						context: 'petition_dashboard_group_rec',
						viewContext,
					},
				} as FacebookPost | RedditPost);
			} else {
				window.open(rec.url, '_blank', 'noopener,noreferrer');
			}
		},
		[
			platform,
			trackLinkClick,
			startSharingFlow,
			viewContext,
			petition,
			starterExperienceReadyToSharePredrafted.variation,
		],
	);

	return {
		handleLinkClick,
	};
}
